'use client';

/**
 * AiLoader is the AI busy indicator: an animated icon beside a shimmering `title` that names the
 * work, with an optional elapsed timer alongside it and a muted subtitle reporting progress
 * underneath.
 *
 * Each line owns its slot. The title is caller-controlled, so changing the string swaps it with a
 * soft vertical slide (RotatingText). The subtitle reveals and collapses with a height transition,
 * leaving the title and the icon exactly where they were, so a loader can pick up a longer job
 * mid-flight without anything jumping.
 *
 * Several props drive the subtitle, resolved by precedence: `slowText` once the work overruns,
 * `stages` stepped by the clock, `messages` cycled on an interval, a controlled `subtitle`, or a
 * `hint` after a short delay. With none of them set the loader is just the title row.
 */

import { useRef, type ReactNode } from 'react';

import { cn } from '../../../lib/utils/cn';
import { AiLoaderFrame } from './ai-loader-frame';
import { RotatingText } from './rotating-text';
import { useLoaderStatus, type LoaderSubtitleOptions } from './use-loader-status';
import styles from './ai-loader.module.css';

export interface AiLoaderProps extends LoaderSubtitleOptions {
  /** The first line, naming the work. Changing it slides the new title across. */
  title: string;
  /** Every title this loader may show. Pins the title to the widest of them, so a timer beside it
   *  holds its place while the title changes. */
  reserveTitles?: string[];
  /** Show an elapsed timer beside the title. */
  showTimer?: boolean;
  /** Leading icon. Defaults to the animated AiLoaderIcon; pass a node for a static glyph. */
  icon?: ReactNode;
  /** Shimmer the title. On by default. */
  shimmer?: boolean;
  /** Shimmer the subtitle too. Off by default, which leaves it muted. */
  shimmerSubtitle?: boolean;
  className?: string;
}

export const AiLoader = ({
  title,
  reserveTitles,
  showTimer = false,
  icon,
  shimmer = true,
  shimmerSubtitle = false,
  className,
  ...subtitleOptions
}: AiLoaderProps) => {
  const { subtitle, time, reserveFor } = useLoaderStatus(subtitleOptions);

  // Hold the last subtitle so the row can animate closed against real content instead of snapping.
  const lastSubtitle = useRef('');
  if (subtitle) lastSubtitle.current = subtitle;

  return (
    <AiLoaderFrame icon={icon} className={cn('flex flex-col', className)}>
      <div className="flex items-baseline gap-2">
        <RotatingText
          text={title}
          reserveFor={reserveTitles}
          className={cn('typo-base', shimmer ? styles['shimmer'] : 'text-space')}
        />
        {showTimer && <span className="typo-xs text-space-40 tabular-nums">{time}</span>}
      </div>

      <div className={cn(styles['reveal'], subtitle && styles['revealOpen'])}>
        <div className="min-h-0 min-w-0 overflow-hidden">
          {lastSubtitle.current && (
            <RotatingText
              text={subtitle || lastSubtitle.current}
              reserveFor={reserveFor}
              className={cn('typo-xs', shimmerSubtitle ? styles['shimmer'] : 'text-space-40')}
            />
          )}
        </div>
      </div>
    </AiLoaderFrame>
  );
};
